import {
  Component,
  ChangeDetectionStrategy,
  ElementRef,
  ChangeDetectorRef,
  ContentChild,
  AfterContentInit,
  ContentChildren,
  QueryList,
  ViewEncapsulation,
  Input,
} from '@angular/core';
import { FormField, FormFieldCtor, mixinFormField } from './form-field.helper';
import {
  APP_ERROR,
  APP_LABEL,
  APP_PREFIX,
  APP_SUBLABEL,
  APP_SUFFIX,
  APP_WARN,
  ErrorDirective,
  FormFieldControlDirective,
  LabelDirective,
  PrefixDirective,
  SubLabelDirective,
  SuffixDirective,
  WarnDirective,
} from './directives';
import { debounceTime, distinctUntilChanged, pipe, startWith } from 'rxjs';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';

const FormFieldBase: FormFieldCtor = mixinFormField(
  class {
    constructor(
      public _elementRef: ElementRef,
      public _changeDetectorRef: ChangeDetectorRef,
    ) {}
  },
);

@UntilDestroy()
@Component({
  selector: 'app-form-field',
  templateUrl: './form-field.component.html',
  styleUrls: ['./form-field.component.scss'],
  host: {
    class: 'app-form-field',
    '[class.border]': 'border',
    '[class.focused]': 'control.focused',
    '[class.filled]': 'checkFilled()',
    '[class.error]': 'errorChildren?.length && control.touched && !control.focused || checkTouched',
    '[class.warn]': '!checkFilled() && control.touched && !control.focused && warn || isSoftInValid',
    '[class.disabled]': 'disabled',
  },
  inputs: ['hideError', 'small', 'class'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.Default
})
export class FormFieldComponent extends FormFieldBase implements AfterContentInit, FormField {
  @Input() disabled: boolean = false;
  @Input() checkTouched: boolean = false;
  @Input() isSoftInValid: boolean = false;
  @Input() warn: boolean = false;
  @Input() border: boolean = false;
  @Input() isContainerMode: boolean = false;
  @ContentChild(FormFieldControlDirective) control!: FormFieldControlDirective<any>;
  @ContentChildren(APP_ERROR, { descendants: true }) errorChildren?: QueryList<ErrorDirective>;
  @ContentChildren(APP_WARN, { descendants: true }) warnChildren?: QueryList<WarnDirective>;
  @ContentChildren(APP_LABEL, { descendants: true }) labelChildren?: QueryList<LabelDirective>;
  @ContentChildren(APP_PREFIX, { descendants: true }) prefixChildren?: QueryList<PrefixDirective>;
  @ContentChildren(APP_SUFFIX, { descendants: true }) suffixChildren?: QueryList<SuffixDirective>;
  @ContentChildren(APP_SUBLABEL, { descendants: true }) subLabelChildren?: QueryList<SubLabelDirective>;

  isShowedPassword: boolean = false;

  constructor(
    public elementRef: ElementRef,
    public changeDetectorRef: ChangeDetectorRef,
  ) {
    super(elementRef, changeDetectorRef);
  }

  ngAfterContentInit() {
    if (this.control.controlType) {
      this.elementRef.nativeElement.classList.add(this.control.controlType);
    }

    this.control.stateChanges
      ?.pipe(startWith(null), debounceTime(500), distinctUntilChanged(), untilDestroyed(this))
      .subscribe(() => {
        this.changeDetectorRef.markForCheck();
      });
  }

  checkFilled(): boolean {
    if (this.control.controlType === 'select') {
      if (typeof this.control.value === 'object') {
        return this.control.value?.length;
      } else {
        return this.control.value !== undefined && this.control.value !== null;
      }
    } else {
      return this.control.value;
    }
  }

  switchShowedPassword() {
    this.isShowedPassword = !this.isShowedPassword;
    this.control.type = this.isShowedPassword ? 'text' : 'password';
  }

  setPasswordIconClass(): string {
    return this.isShowedPassword ? 'icon-eye' : 'icon-eye-hide';
  }
}
